<template>
  <div
    ref="playerElement"
    class="vimeo-player"
  />
</template>

<script setup lang="ts">
  const props = defineProps<{
    videoId: string | number | null
    autoplay?: boolean
    muted?: boolean
    loop?: boolean
    start?: number
    end?: number
    showControls?: boolean
    color?: string
    quality?: string
  }>()

  const emit = defineEmits({
    ready: (player: any) => true,
    playing: () => true,
    paused: () => true,
    ended: () => true,
    error: (error: any) => true,
    timeupdate: (data: { currentTime: number; duration: number }) => true,
  })

  const playerElement = ref()
  let player: any = null
  let loopInterval: NodeJS.Timeout | null = null

  const { onLoaded } = useScriptVimeoPlayer()

  // Custom loop function
  const startLoopCheck = () => {
    if (!props.loop || !props.end || !player) return

    loopInterval = setInterval(async () => {
      try {
        const currentTime = await player.getCurrentTime()
        if (currentTime >= props.end) {
          await restartVideo()
        }
      } catch (error) {
        console.warn('Loop check error:', error)
      }
    }, 500) // Check every 500ms
  }

  const stopLoopCheck = () => {
    if (loopInterval) {
      clearInterval(loopInterval)
      loopInterval = null
    }
  }

  const restartVideo = async () => {
    if (!player) return
    try {
      const startTime = props.start || 0
      await player.setCurrentTime(startTime)
      await player.play()
    } catch (error) {
      console.warn('Restart video error:', error)
    }
  }

  onLoaded(async ({ Vimeo }) => {
    await nextTick()
    const options = {
      id: Number(props.videoId),
      autoplay: props.autoplay,
      muted: props.muted,
      loop: props.loop && !props.end, // Use native loop only if no custom end time
      controls: props.showControls,
      color: props.color,
      quality: props.quality,
      responsive: true,
      dnt: true, // Do Not Track for privacy
    }

    // Add start time if provided
    if (props.start && props.start > 0) {
      options.start = props.start
    }

    try {
      player = new Vimeo.Player(playerElement.value, options)

      // Set up event listeners
      player.on('loaded', async () => {
        try {
          emit('ready', player)

          // If we have a start time, seek to it after loading
          if (props.start) {
            await player.setCurrentTime(props.start)
          }
        } catch (loadError) {
          console.error('Error in loaded event:', loadError)
          emit('error', loadError)
        }
      })

      player.on('play', () => {
        emit('playing')
        if (props.loop && props.end) {
          startLoopCheck()
        }
      })

      player.on('pause', () => {
        emit('paused')
        stopLoopCheck()
      })

      player.on('ended', () => {
        emit('ended')
        stopLoopCheck()
        if (props.loop) {
          restartVideo()
        }
      })

      player.on('error', (error) => {
        console.error('Vimeo player error:', error)
        stopLoopCheck()

        const enhancedError = {
          ...error,
          videoId: props.videoId,
          message: error.message || `Video ${props.videoId} could not be loaded`,
        }

        emit('error', enhancedError)
      })

      player.on('timeupdate', async (data) => {
        try {
          emit('timeupdate', {
            currentTime: data.seconds,
            duration: await player.getDuration(),
          })
        } catch (timeError) {
          console.warn('Error getting duration:', timeError)
        }
      })
    } catch (error) {
      console.error('Error initializing Vimeo player:', error)

      const enhancedError = {
        originalError: error,
        videoId: props.videoId,
        message: `Failed to initialize player for video ${props.videoId}. Please check if the video ID is valid and the video exists.`,
      }

      emit('error', enhancedError)
    }
  })

  // Cleanup on unmount
  onUnmounted(() => {
    stopLoopCheck()
    if (player && typeof player.destroy === 'function') {
      player.destroy().then(() => {
        player = null
      })
    }
  })

  // Expose methods - all return promises like the Vimeo API
  defineExpose({
    play: () => player?.play(),
    pause: () => player?.pause(),
    mute: () => player?.setMuted(true),
    unMute: () => player?.setMuted(false),
    setVolume: (volume: number) => player?.setVolume(volume / 100), // Vimeo uses 0-1 scale
    getCurrentTime: () => player?.getCurrentTime(),
    setCurrentTime: (time: number) => player?.setCurrentTime(time),
    getDuration: () => player?.getDuration(),
    getVideoTitle: () => player?.getVideoTitle(),
  })
</script>

<style scoped>
  .vimeo-player {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .vimeo-player :deep(iframe) {
    width: 100%;
    height: 100%;
  }
</style>
